import { useCallback } from 'react'

import { Editor } from '@tiptap/react'

import { Post } from '@tribeplatform/gql-client/types'
import { usePost, useUpdatePost } from '@tribeplatform/react-sdk/hooks'

import { getMappingFields } from '../../utils/post'
import { PostContent } from '../Post/PostContent'
import { useAttachments } from '../TipTap/hooks/useAttachments'
import { canQuitEditor } from '../TipTap/utils/state'
import { ReplyEditor, ReplyEditorProps } from './ReplyEditor'

export const ReplyContent = ({
  reply,
  edit,
  onEdit,
  onCancel,
}: {
  reply: Post
  edit: boolean
  onEdit: () => void
} & Pick<ReplyEditorProps, 'onCancel'>) => {
  const { mutate: updatePost, isLoading } = useUpdatePost()
  const {
    attachments,
    resetAttachments,
    handleAttachmentSelect,
    handleAttachmentDelete,
  } = useAttachments(reply.attachments)
  const { data: fullReply, isFetching: fetchingFullPost } = usePost({
    id: reply.id,
    useQueryOptions: {
      enabled: edit,
    },
  })

  const handleCancel = useCallback(
    isTouched => {
      if (
        canQuitEditor({
          isTouched,
          attachments,
          initialAttachments: reply.attachments,
        })
      ) {
        resetAttachments()
        onCancel(isTouched)
      }
    },
    [attachments, onCancel, reply.attachments, resetAttachments],
  )
  const loading = isLoading || fetchingFullPost

  const update = useCallback(
    async (content, editor: Editor) => {
      const _content = content || ''
      const postType = reply?.postType || fullReply?.postType
      const mappingFields = getMappingFields({
        content: _content,
        postTypeName: postType.name,
      })

      updatePost(
        {
          id: reply.id,
          input: {
            mappingFields,
            publish: true,
            attachmentIds: attachments.map(({ id }) => id),
          },
        },
        {
          onSuccess: () => {
            editor.commands.clearContent()
            onEdit?.()
          },
        },
      )
    },
    [
      reply?.postType,
      reply.id,
      fullReply?.postType,
      updatePost,
      attachments,
      onEdit,
    ],
  )

  if (edit) {
    return (
      <ReplyEditor
        attachments={attachments}
        onAttachmentSelect={handleAttachmentSelect}
        onAttachmentDelete={handleAttachmentDelete}
        reply={reply}
        onCancel={handleCancel}
        isLoading={loading}
        onReply={update}
      />
    )
  }
  return <PostContent post={reply} />
}
